/**
 * @name BackupDialog
 * @description Modal para añadir un backup
 * @author Gerardo de León
 */

import React, { Component } from 'react';
import i18n from 'i18next';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Formik } from 'formik';
import moment from 'moment';
import styled from 'styled-components';

import { Environment } from '@config';
import { EmployeeDetail } from '@components';
import { EmployeeService, BackupService, NotificationsService, ResponseHandlerService } from '@services';
import { Icons, Layout, MUI, Typography } from '@styles';

class BackupDialog extends Component {
  constructor(props) {
    super(props);
    this.userInfo = JSON.parse(localStorage.getItem('ls.infoUsuario')).Empleado;
    this.state = {
      employeesList: [],
      backup: '',
      backupIdRequest: 0,
      backupDetail: {
        id: 0,
        name: '',
        photo: '',
        job: '',
      },
      startDate: moment(new Date()).add(1, 'days').startOf('day'),
      endDate: moment(new Date()).add(1, 'days').startOf('day'),
      comment: '',
    };

    this.schema = Yup.object({
      startDate: Yup.object().nullable().required('*'),
      endDate: Yup.object().nullable().required('*'),
      comment: Yup.string()
        .min(2, i18n.t('FormsGeneralTexts.min2Chars'))
        .max(250, i18n.t('FormsGeneralTexts.max250Chars'))
        .required('*')
    });
  }

  componentDidMount() {
    this.getEmployees();
  }

  /**
   * Consulta todos los empleados en estatus 'Alta'
   */
  getEmployees = () => {
    EmployeeService.get({ page: 0, size: 0, filter: { estatus: true } })
      .then((response) => {
        let employeesList = response.filter((employee) => employee.IdEmpleado !== this.userInfo.IdEmpleado);
        this.setState({ employeesList });
      })
      .catch(() => { });
  }

  /**
   * Seleciona un empleado de backup
   * @param {object} employee
   */
  setBackup = (employee) => {
    let employeeSelect = {
      id: employee ? employee.IdEmpleado : 0,
      name: employee ? employee.Nombre : '',
      photo: employee ? employee.Foto : '',
      job: employee ? employee.Puesto : '',
    };

    this.setState({ backup: employee, backupDetail: employeeSelect });
  }

  onAdd = (values, resetForm, setSubmitting) => {
    const { backupDetail } = this.state;

    let request = {
      IdEmpleadoBackup: backupDetail.id,
      FechaInicio: new Date(values.startDate),
      FechaFin: new Date(values.endDate),
      Comentario: values.comment,
    };

    // Notificación - <colaborador> te ha asignado como su backup del <fechaInicio> al <fechaFin>
    let notificationToSend = {
      IdAmbiente: Environment.getCurrent().environmentId,
      URL: '#/configuraperfil',
      IdTipoNotificacion: 129,
      IdEmpleado: backupDetail.id,
      Parametros: JSON.stringify({ Tab: 6 }),
      Propiedades: JSON.stringify({
        colaborador: backupDetail.name,
        fechaInicio: values.startDate.format('DD/MM/YYYY'),
        fechaFin: values.endDate.format('DD/MM/YYYY')
      })
    };

    BackupService.post(request)
      .then((response) => {
        setSubmitting(false);
        resetForm();
        this.props.onClose();
        this.props.getBackup();
        NotificationsService.post(notificationToSend);
        ResponseHandlerService.success(response);
      })
      .catch((error) => {
        setSubmitting(false);
        ResponseHandlerService.error(error);
      });
  }

  render() {
    const {
      employeesList,
      backup,
      backupDetail,
      startDate,
      endDate,
      comment,
    } = this.state;

    const { open, onClose, } = this.props;

    return (
      <MUI.Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
      >
        <MUI.DialogTitle>
          {`${i18n.t('GenericMessages.add')} ${i18n.t('Backup').toLowerCase()}`}
          <MUI.DialogBoxClose onClick={onClose}>
            <Icons.Close />
          </MUI.DialogBoxClose>
        </MUI.DialogTitle>

        <Formik
          enableReinitialize
          initialValues={{ startDate, endDate, comment }}
          validationSchema={this.schema}
          isInitialValid={this.schema.isValidSync({ startDate, endDate, comment })}
          onSubmit={(values, { resetForm, setSubmitting }) => {
            this.onAdd(values, resetForm, setSubmitting);
          }}
        >
          {(formikProps) => {
            return (
              <>
                <MUI.DialogContent>
                  <MUI.Box padding="8px">
                    <Typography.H6>{i18n.t('BackupSettings.whoYourBackup')}</Typography.H6>
                  </MUI.Box>
                  {/* Seleccionar Backup */}
                  <MUI.Grid container alignItems="center">
                    {/* Colaborador */}
                    <MUI.Grid item md={6} sm={6} xs={12}>
                      <MUI.Box padding="8px">
                        <MUI.Autocomplete
                          label={i18n.t('GenericMessages.employee')}
                          searchList={employeesList}
                          searchBy="Nombre"
                          value={backup}
                          onChange={(newValue) => this.setBackup(newValue)}
                        />
                      </MUI.Box>
                    </MUI.Grid>
                    {/* Avatar Colaborador */}
                    <MUI.Grid item md={6} sm={6} xs={12}>
                      <MUI.Box padding="8px">
                        <EmployeeDetail
                          avatar={backupDetail.photo}
                          avatarSize={'md'}
                          employeeName={backupDetail.name}
                          employeeDetail={backupDetail.job}
                        />
                      </MUI.Box>
                    </MUI.Grid>
                  </MUI.Grid>

                  {/* Periodo Backup */}
                  <MUI.Box padding="8px">
                    <Typography.H6>{i18n.t('BackupSettings.periodApply')}</Typography.H6>
                  </MUI.Box>
                  <MUI.Grid container alignItems="center">
                    {/* Fecha Inicio */}
                    <MUI.Grid item md={6} sm={6} xs={12}>
                      <MUI.Box padding="8px">
                        <MUI.DatePicker
                          allowKeyboard={true}
                          label={i18n.t('GenericMessages.startDate')}
                          orientation={'landscape'}
                          format={i18n.t('FormatDates.short')}
                          invalidDateMessage={i18n.t('FormatDates.invalidDate')}
                          minDate={moment().add(1, 'days')}
                          minDateMessage={i18n.t('FormatDates.invalidMinDate')}
                          value={formikProps.values.startDate}
                          onChange={(newDate) => formikProps.setFieldValue('startDate', newDate)}
                          openTo="date"
                        />
                      </MUI.Box>
                    </MUI.Grid>
                    {/* Fecha Fin */}
                    <MUI.Grid item md={6} sm={6} xs={12}>
                      <MUI.Box padding="8px">
                        <MUI.DatePicker
                          allowKeyboard={true}
                          label={i18n.t('GenericMessages.endDate')}
                          value={formikProps.values.endDate}
                          orientation={'landscape'}
                          format={i18n.t('FormatDates.short')}
                          invalidDateMessage={i18n.t('FormatDates.invalidDate')}
                          minDate={moment().add(1, 'days')}
                          minDateMessage={i18n.t('FormatDates.invalidMinDate')}
                          onChange={(newDate) => formikProps.setFieldValue('endDate', newDate)}
                          openTo="date"
                        />
                      </MUI.Box>
                    </MUI.Grid>
                  </MUI.Grid>
                  {/* Comentario */}
                  <MUI.Grid item md={12} sm={12}>
                    <MUI.Box padding="8px">
                      <MUI.TextField
                        multiline
                        label={i18n.t('GenericMessages.comment')}
                        type="text"
                        name="comment"
                        value={formikProps.values.comment}
                        characterLimit={250}
                        onBlur={formikProps.handleBlur}
                        onChange={formikProps.handleChange}
                        error={Boolean(formikProps.touched.comment && formikProps.errors.comment)}
                        helperText={formikProps.touched.comment && formikProps.errors.comment}
                        inputProps={{ maxLength: '250' }}
                      />
                    </MUI.Box>
                  </MUI.Grid>
                </MUI.DialogContent>

                <MUI.DialogActions>
                  <Layout.BoxRequired>
                    {`* ${i18n.t('FormsGeneralTexts.required')}`}
                  </Layout.BoxRequired>
                  <MUI.ButtonText onClick={onClose}>
                    {i18n.t('DialogConfirm.cancel')}
                  </MUI.ButtonText>
                  <MUI.ButtonCTA type="button" color="primary" disabled={!formikProps.isValid || formikProps.isSubmitting || !backup} onClick={formikProps.handleSubmit}>
                    {i18n.t('DialogConfirm.save')}
                  </MUI.ButtonCTA>
                </MUI.DialogActions>
              </>
            );
          }}
        </Formik>
      </MUI.Dialog>
    );
  }
}

BackupDialog.propTypes = {
  // Boolean que indica si el modal esta abierto.
  open: PropTypes.bool.isRequired,
  // Función que se ejecuta cuando se cierra el modal.
  onClose: PropTypes.func.isRequired,
  // Función que se ejecuta para actualizar los backup.
  getBackup: PropTypes.func.isRequired,
};

export default BackupDialog;
